// Feedback / change-request API client. Used by the FeedbackWidget
// mounted globally in DashboardLayout.
//
// Visibility rules (enforced server-side):
//   - admins see all feedback
//   - operators see only their own
//
// Any authenticated user can submit. Status is binary: 'open' | 'resolved'.

import { request } from './client'

export type FeedbackStatus = 'open' | 'resolved'

export interface FeedbackItem {
  id: number
  user_id: number
  author_name: string | null
  page_url: string
  body: string
  status: FeedbackStatus
  created_at: string
  resolved_at: string | null
  resolved_by: number | null
  resolver_name: string | null
}

export const feedback = {
  /** List feedback. Admins see all; operators see only their own. */
  list: (filter: { status?: FeedbackStatus | 'all'; pageUrl?: string } = {}) => {
    const params = new URLSearchParams()
    if (filter.status) params.set('status', filter.status)
    if (filter.pageUrl) params.set('page_url', filter.pageUrl)
    const qs = params.toString()
    return request<FeedbackItem[]>(`/feedback${qs ? `?${qs}` : ''}`)
  },

  /** Count of open feedback items visible to the current user. */
  count: () => request<{ open_count: number }>(`/feedback/count`),

  /** Create a new feedback item tied to the page the user is currently on. */
  create: (body: string, pageUrl: string) =>
    request<FeedbackItem>('/feedback', {
      method: 'POST',
      body: JSON.stringify({ body, page_url: pageUrl }),
    }),

  /** Toggle status between 'open' and 'resolved'. */
  setStatus: (id: number, status: FeedbackStatus) =>
    request<FeedbackItem>(`/feedback/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  /** Delete a feedback item. Owner or admin only. */
  delete: (id: number) =>
    request<{ status: string; id: number }>(`/feedback/${id}`, {
      method: 'DELETE',
    }),
}
